/**
 * Toast notification system — Sonner-backed adapter (Phase 5.3a).
 *
 * Preserves the existing useToast() API while rendering via Sonner
 * (from dads-components / sonner package). This is a drop-in replacement
 * — no consumer changes required.
 *
 * Usage:
 *   // Wrap app in <ToastProvider>
 *   // In any component:
 *   const toast = useToast();
 *   toast.success('Training run started');
 *   toast.error('Failed to create role');
 */

import { type ReactNode } from 'react'
import { toast as sonnerToast, Toaster } from 'sonner'

interface ToastContextValue {
  success: (message: string) => void
  error: (message: string) => void
  warning: (message: string) => void
  info: (message: string) => void
}

// Sonner-backed implementation — no context needed since sonner uses
// a global toast queue. The useToast() hook returns stable references.
const toastFns: ToastContextValue = {
  success: (message: string) => sonnerToast.success(message, { duration: 4000 }),
  error: (message: string) => sonnerToast.error(message, { duration: 8000 }),
  warning: (message: string) => sonnerToast.warning(message, { duration: 5000 }),
  info: (message: string) => sonnerToast.info(message, { duration: 4000 }),
}

/**
 * Hook to show toast notifications from any component.
 *
 * Backed by Sonner — works with or without ToastProvider in the tree,
 * though ToastProvider must be mounted for rendering.
 */
export function useToast(): ToastContextValue {
  return toastFns
}

/**
 * Wraps the app to provide toast rendering.
 *
 * Mounts the Sonner Toaster component with dark-first C2 aesthetic styling.
 * Position: bottom-right (matches previous custom implementation).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          classNames: {
            toast: 'bg-surface border-border shadow-theme-lg text-content',
            title: 'text-content text-sm font-medium',
            description: 'text-content-secondary text-sm',
          },
        }}
      />
    </>
  )
}
